import { useTranslation } from "react-i18next";
import { useState } from "react";
import { MoreVertical, Ban, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

interface BlockReportButtonProps {
  userId: string;
  username: string;
}

export const BlockReportButton = ({
 userId, username }: BlockReportButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<"block" | "report">("block");
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const queryClient = useQueryClient();

  // Get current user
  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Check if user is already blocked
  const { data: isBlocked } = useQuery({
    queryKey: ["is-blocked", currentUser?.id, userId],
    queryFn: async () => {
      if (!currentUser) return false;
      const { data } = await supabase
        .from("blocked_users")
        .select("id")
        .eq("user_id", currentUser.id)
        .eq("blocked_user_id", userId)
        .maybeSingle();
      return !!data;
    },
    enabled: !!currentUser && !!userId,
  });

  const blockMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("block-user", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { blockedUserId: userId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(`@${username} has been blocked`);
      // Invalidate all blocking-related queries
      queryClient.invalidateQueries({ queryKey: ["is-blocked"] });
      queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
      queryClient.invalidateQueries({ queryKey: ["blocking-status"] });
      // Also invalidate friends and leaderboard to refresh immediately
      queryClient.invalidateQueries({ queryKey: ["friends-list"] });
      queryClient.invalidateQueries({ queryKey: ["pending-friend-requests"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      setOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to block user");
    },
  });

  const unblockMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("unblock-user", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { blockedUserId: userId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(`@${username} has been unblocked`);
      // Invalidate all blocking-related queries
      queryClient.invalidateQueries({ queryKey: ["is-blocked"] });
      queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
      queryClient.invalidateQueries({ queryKey: ["blocking-status"] });
      // Also invalidate friends and leaderboard to refresh immediately
      queryClient.invalidateQueries({ queryKey: ["friends-list"] });
      queryClient.invalidateQueries({ queryKey: ["pending-friend-requests"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      setOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to unblock user");
    },
  });

  const reportMutation = useMutation({
    mutationFn: async () => {
      if (!reportReason) {
        throw new Error("Please select a reason for reporting");
      }
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("report-user", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: {
          reportedUserId: userId,
          reason: reportReason,
          details: reportDetails || null,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(t('notifications.toast.report_submitted_thank_you_for_helping_keep_our_co'));
      setOpen(false);
      setReportReason("");
      setReportDetails("");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to submit report");
    },
  });

  const handleSubmit = () => {
    if (action === "block") {
      if (isBlocked) {
        unblockMutation.mutate();
      } else {
        blockMutation.mutate();
      }
    } else {
      reportMutation.mutate();
    }
  };

  const isPending = blockMutation.isPending || reportMutation.isPending || unblockMutation.isPending;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="h-8 w-8"
        title={isBlocked ? t('block_report.unblock_button_title') : t('block_report.block_button_title')}
      >
        <MoreVertical className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('block_report.title', { username })}</DialogTitle>
            <DialogDescription>{t('ui.text.choose_an_action_to_take_for_this_user')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup value={action} onValueChange={(value) => setAction(value as "block" | "report")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="block" id="block" />
                <Label htmlFor="block" className="flex items-center gap-2 cursor-pointer">
                  <Ban className="h-4 w-4" />
                  {isBlocked ? t('block_report.unblock_user') : t('block_report.block_user')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="report" id="report" />
                <Label htmlFor="report" className="flex items-center gap-2 cursor-pointer">
                  <AlertTriangle className="h-4 w-4" />{t('ui.text.report_user')}</Label>
              </div>
            </RadioGroup>

            {action === "block" && (
              <div className="rounded-lg bg-muted p-3 text-sm">
                {isBlocked ? (
                  <p>
                    {t('block_report.unblock_description', { username })}
                  </p>
                ) : (
                  <p>
                    {t('block_report.block_description', { username })}
                  </p>
                )}
              </div>
            )}

            {action === "report" && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="reason">{t('ui.text.reason')}</Label>
                  <RadioGroup value={reportReason} onValueChange={setReportReason}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="spam" id="spam" />
                      <Label htmlFor="spam" className="cursor-pointer font-normal">{t('ui.text.spam_or_misleading_content')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="harassment" id="harassment" />
                      <Label htmlFor="harassment" className="cursor-pointer font-normal">{t('ui.text.harassment_or_bullying')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="inappropriate" id="inappropriate" />
                      <Label htmlFor="inappropriate" className="cursor-pointer font-normal">{t('ui.text.inappropriate_content')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="impersonation" id="impersonation" />
                      <Label htmlFor="impersonation" className="cursor-pointer font-normal">
                        {t('common.impersonation')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="other" id="other" />
                      <Label htmlFor="other" className="cursor-pointer font-normal">
                        {t('common.other')}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="details">{t('ui.text.additional_details_optional')}</Label>
                  <Textarea
                    id="details"
                    placeholder={t('forms.placeholders.please_provide_any_additional_context')}
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="rounded-lg bg-muted p-3 text-sm">
                  <p>{t('ui.text.reports_are_reviewed_by_our_team_false_reports_may')}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isPending || (action === "report" && !reportReason)}
              variant={action === "report" ? "destructive" : "default"}
            >
              {isPending ? t('common.processing') : action === "block" ? (isBlocked ? t('common.unblock') : t('common.block')) : t('common.submit_report')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
